import { Divider, Stack } from 'component-library';
import { FC, HTMLAttributes, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { LandscapeType, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { Dot } from '@/components/Charts/components/Dot';
import {
  ChartTooltip,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';
import { getLandscapeTypeColor } from '@/utils/getLandscapeTypeColor';

import { Bar, BarChart, Chart, getXAxisProps, TooltipProps } from './components/Chart';
import { ChartLegendBar } from './components/LegendBar';
import { ChartLegendBarItem } from './components/types';
import { NoChartData } from './NoChartData';
import { DefaultChartProps, GraphFact } from './types';
import { getYearDomainForNumOfYearsFromToday } from './utils';

const UNIT = UnitEnum['%'];

const SHARED_BAR_PROPS = {
  stackId: 'land-cover-change',
  isAnimationActive: false,
  barSize: 21,
  stroke: '#fff',
  strokeWidth: 1,
};

export const LandCoverChangeChart: FC<
  HTMLAttributes<HTMLDivElement> & DefaultChartProps & { data: PlotReportFactElement<GraphFact[]> }
> = ({ data, ...delegated }) => {
  const chartConfig = useGetChartElementsConfig();

  const chartData = useChartData(data);

  const sortedChartData = chartData.sort((a, b) => a.label - b.label);

  const landscapeTypesBars = getLandscapeTypesBars(data);

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = getYearDomainForNumOfYearsFromToday(
    0,
    0,
  )([new Date(firstTick.label).getFullYear(), new Date(lastTick.label).getFullYear()]);

  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    tickFormatter: printYear,
  };

  return (
    <>
      <Chart.Container {...delegated} config={chartConfig} data={sortedChartData}>
        <BarChart data={sortedChartData}>
          {landscapeTypesBars.map((item) => (
            <Bar
              key={item.dataKey}
              {...item}
              {...SHARED_BAR_PROPS}
              activeBar={(props: unknown) => <CustomActiveBar {...(props as ActiveBarProps)} />}
              className={`land-cover-change-${item.dataKey}`}
            />
          ))}
          <Chart.YAxis domain={[0, 100]} className='land-cover-change-y-axis'>
            <Chart.Label>{UNIT}</Chart.Label>
          </Chart.YAxis>
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='land-cover-change-x-axis' />
          <Chart.Tooltip content={<CustomTooltip />} cursor={false} />
        </BarChart>
      </Chart.Container>
      <CustomLegendBar landscapeTypes={landscapeTypesBars.map((p) => p.dataKey)} />
    </>
  );
};
const useGetChartElementsConfig = () => {
  const { t } = useTranslation();

  return Object.fromEntries(
    Object.values(LandscapeType).map((landscapeType) => [
      landscapeType,
      {
        label: t(`global.landscapeTypes.${landscapeType as LandscapeType}`),
        color: getLandscapeTypeColor(landscapeType as LandscapeType),
      },
    ]),
  );
};

type ChartDataEntry = {
  [K in LandscapeType]?: number;
} & { plotLabel: string; label: number };

const useChartData = (data: PlotReportFactElement<GraphFact[]>) => {
  const { t } = useTranslation();

  return data.value.reduce<ChartDataEntry[]>((acc, cur) => {
    const { labels = [], date } = cur;

    const landTypeLabel = labels.find((l) => l.name === PlotReportFactLabelNameEnum.landscape_type)?.value as
      | LandscapeType
      | undefined;

    if (!landTypeLabel || !date) return acc;

    const plotTypeLabel = t(`global.landscapeTypes.${landTypeLabel}`);

    const label = new Date(date).getTime();

    const existingEntry = acc.find((entry) => entry.label === label);

    if (existingEntry) {
      existingEntry[`${landTypeLabel}`] = cur.value;
    } else {
      acc.push({
        [`${landTypeLabel}`]: cur.value,
        plotLabel: plotTypeLabel,
        label,
      });
    }
    return acc;
  }, []);
};

const getLandscapeTypesBars = (data: PlotReportFactElement<GraphFact[]>) => {
  const landTypes = new Set(
    data.value.flatMap((item) => {
      const landTypeLabel = item?.labels?.find((l) => l.name === PlotReportFactLabelNameEnum.landscape_type)?.value as
        | LandscapeType
        | undefined;
      return landTypeLabel ?? [];
    }),
  );

  return Array.from(landTypes).map((landType) => {
    return {
      dataKey: landType,
      fill: getLandscapeTypeColor(landType),
      className: `land-type-${landType}`,
    };
  });
};

type ActiveBarProps = SVGProps<SVGElement> & {
  payload: ChartDataEntry;
  value: number[];
  dataKey: string;
  background: { y: number };
};

const CustomActiveBar = ({ x, y, width, height, payload, value, dataKey, background }: ActiveBarProps) => {
  if (!x || !y || !width || !height) return <></>;

  const topBarValue = Object.values(LandscapeType).reduce((acc, key) => {
    if (key in payload && payload[`${key}`]) {
      return acc + (payload[`${key}`] ?? 0);
    }

    return acc;
  }, 0);

  const isTopBar = value.at(-1) === topBarValue;

  const barMiddleX = (x as number) + (width as number) / 2;
  const barBottom = (y as number) + (height as number);
  const barTop = isTopBar ? background.y : y;

  const dotProps = {
    cx: barMiddleX,
    cy: y as number,
    fill: '#000000',
    strokeWidth: 0,
    opacity: 1,
  };

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={getLandscapeTypeColor(dataKey as LandscapeType)}
        stroke={SHARED_BAR_PROPS.stroke}
      />
      <line x1={barMiddleX} x2={barMiddleX} y1={barTop} y2={barBottom} stroke={colorPalette.divider} strokeWidth={1} />
      {isTopBar && (
        <Dot.Container {...dotProps}>
          <Dot.Active {...dotProps} fill='#000000' />
        </Dot.Container>
      )}
    </g>
  );
};

const CustomLegendBar = ({ landscapeTypes }: { landscapeTypes: LandscapeType[] }) => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = landscapeTypes.map((landscapeType) => ({
    label: t(`global.landscapeTypes.${landscapeType}`),
    color: getLandscapeTypeColor(landscapeType),
    shape: 'column',
  }));

  return (
    <ChartLegendBar className='sm:ml-[28px]' items={legendBarItems} data-testid='land-cover-change-chart-legend' />
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload || payload.length === 0) return null;

  const tooltipContent = payload.map((entry) => {
    return {
      label: t(`global.landscapeTypes.${entry.name as LandscapeType}`),
      value: !isNullish(entry?.value)
        ? `${getDisplayNumber(entry?.value as string)} ${UNIT}`
        : t('global.analysis.noData'),
    };
  });

  return (
    <ChartTooltip className='ml-1'>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
      </Stack>
      {tooltipContent.map((landType, i) => {
        return (
          <div key={landType.label}>
            <Stack direction='row' spacing={8} className='justify-between'>
              <ChartTooltipTextPrimary>{landType.label}</ChartTooltipTextPrimary>
              <ChartTooltipTextPrimary>{landType.value}</ChartTooltipTextPrimary>
            </Stack>
            {i < tooltipContent.length - 1 && <Divider className='mt-2' />}
          </div>
        );
      })}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipLandCoverChange };
